import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import styled from '@emotion/styled';
import type { IconName } from '@hotjar/hotsauce';
import { tokens } from '@hotjar/hotsauce';
import { useState } from 'react';
import type { AllBadgeStatus } from '@hotjar/hotsauce/build/components/Badge/BaseBadge';
import { hideOnEsc } from '../../../../../utils/hideOnEsc';
import { FilterTag } from './FilterTag';
import { useTippy } from '../../../useTippy';

const StyledTippy = styled(Tippy)`
  color: ${tokens.color.text_default};
  ${tokens.elevation(2)};
  background-color: ${tokens.color.ui_background};

  .tippy-content {
    padding: 0;
  }
`;

const LazyTippy = (props: TippyProps) => {
  const [mounted, setMounted] = useState(false);

  const computedProps = { ...props };
  const { content, render } = props;

  if (render) {
    computedProps.render = (...args) => (mounted ? render(...args) : '');
  } else {
    computedProps.content = mounted ? content : '';
  }

  return (
    <StyledTippy
      onMount={() => setMounted(true)}
      onHidden={() => setMounted(false)}
      {...computedProps}
    />
  );
};

interface FilterProps {
  filterName: string;
  label1?: string;
  label2?: string;
  autoFocus?: boolean;
  iconName?: IconName;
  showDropdownCaret?: boolean;
  showDropdownOnMount?: boolean;
  renderDropdown: (options: { close: () => void }) => JSX.Element;
  onRemove?: () => void;
  onDropdownHidden?: () => void;
  onDropdownShown?: () => void;
  isInactive?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
  isAutoSelected?: boolean;
  badge?: AllBadgeStatus;
  isFunnelStep?: boolean;
}

const Z_INDEX = 13; // stay under .sidebar-feature and above .paging-row

export const GenericFilter = ({
  filterName,
  label1,
  label2,
  autoFocus,
  renderDropdown,
  iconName,
  showDropdownCaret,
  showDropdownOnMount,
  onRemove,
  onDropdownHidden,
  onDropdownShown,
  isInactive,
  isDisabled,
  disabledReason,
  isAutoSelected,
  badge,
  isFunnelStep,
}: FilterProps): JSX.Element => {
  const tippy = useTippy();
  const [isDropdownVisible, setIsDropdownVisible] = useState(showDropdownOnMount);
  const focusDropdownContent = () => {
    const dropdownContent = tippy.get()?.popper;
    const firstFocusableElement = dropdownContent?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as HTMLInputElement | null;
    firstFocusableElement?.focus();
  };

  return (
    <LazyTippy
      arrow={false}
      content={renderDropdown({ close: tippy.close })}
      offset={[0, 3]}
      interactive
      onCreate={tippy.set}
      placement="bottom-start"
      theme="custom"
      trigger="click"
      zIndex={Z_INDEX}
      plugins={[hideOnEsc]}
      popperOptions={{
        modifiers: [{ name: 'flip', enabled: false }],
      }}
      appendTo={() => document.body} // To avoid overlapping issue (cf FN-1280)
      onHide={() => {
        setIsDropdownVisible(false);
        onDropdownHidden?.();
      }}
      onShow={() => {
        setIsDropdownVisible(true);
        onDropdownShown?.();
      }}
      showOnCreate={showDropdownOnMount}
      maxWidth="none"
    >
      <FilterTag
        filterName={filterName}
        label1={label1}
        label2={label2}
        autoFocus={autoFocus}
        iconName={iconName}
        showDropdownCaret={showDropdownCaret}
        onRemove={
          onRemove &&
          (() => {
            onRemove();
            tippy.close();
            setIsDropdownVisible(false);
          })
        }
        focusDropdownContent={focusDropdownContent}
        isDropdownVisible={isDropdownVisible}
        isInactive={isInactive}
        isDisabled={isDisabled}
        disabledReason={disabledReason}
        isAutoSelected={isAutoSelected}
        badge={badge}
        isFunnelStep={isFunnelStep}
      />
    </LazyTippy>
  );
};
